//#region Libraries
import { useState, useEffect, useReducer } from "react";
//#endregion
//#region Styles
import { ContainerForm, ContentForm, TitleForm } from "components/styles";
//#endregion
//#region Components
import HelmetUTP from "components/HelmetUTP/HelmetUTP";
import BackToView from "components/BackToView/BackToView";
import StepIndexer from "components/StepIndexer/StepIndexer";
import Step1 from "./components/Steps/Step1/Step1";
import Step2 from "./components/Steps/Step2/Step2";
import Footer from "./components/Footer/Footer";
import ToastMessage from "components/ToastMessage/ToastMessage";
import EventDetail from "../../components/EventDetail/EventDetail";
//#endregion
//#region Reducer
import reducer, { EVENT_FORM_INITIAL_STATE } from "./reducer/event.reducer";
import { TYPES } from "./reducer/event.actions";
//#endregion
//#region Services
import { eventsSessionStorage } from "services/storage/events";
//#endregion
//#region Utils
import useResumeData from "views/Eventos/utils/hooks/useResumeData";
import useDataRelatedToForm from "views/Eventos/utils/hooks/useDataRelatedToForm";
import ABSOLUTE_PATHS from "routes/utils/absolutePaths";
import useToastMessage from "components/ToastMessage/utils/hooks/useToastMessage";
import EventFormContext from "./utils/context/EventFormContext";
import { EVENTS_CONSTANTS } from "config/constants";
//#endregion

const STEPS_DESCRIPTIONS = [
	"Detalles del evento",
	"Organización, ubicación y público",
	"Vista previa y confirmación",
];

const Form = ({ loadingRequest, isCreate = false }) => {
	//#region Reducer
	const [state, dispatch] = useReducer(reducer, EVENT_FORM_INITIAL_STATE);
	//#endregion
	//#region States
	const [canShowDialogLeavingPage, setCanShowDialogLeavingPage] =
		useState(false);
	const [loadedDataRelatedToEventForm, setLoadedDataRelatedToEventForm] =
		useState(false);
	const [mustCorrect, setMustCorrect] = useState(false);
	const [teacherZoomAccountDetail, setTeacherZoomAccountDetail] =
		useState(null);
	//#endregion
	//#region Extra hooks
	const toastMessageSaveEventAsDraft = useToastMessage({
		message: "Hemos guardado tu avance",
		timeoutToHideAfterShow: 2400,
	});
	// Obteniendo datos de apoyo para el formulario
	const [fetchData, dataRelatedToForm] = useDataRelatedToForm({
		loadingRequest,
		setLoadedDataRelatedToEventForm,
	});
	//#endregion
	const {
		allDays,
		locations,
		inscriptionEventTypes,
		allCampus,
		campusEnvironments,
		modalities,
	} = dataRelatedToForm;
	const toStep = step => {
		dispatch({ type: TYPES.TO_STEP, payload: { step } });
	};
	const nextStep = () => {
		dispatch({ type: TYPES.NEXT_STEP });
	};
	// Obtener datos precargados del evento
	const isCompletedForm = form => {
		return {
			step1: form.step1.part2.eventTypeId > 0,
			step2: Boolean(
				form.step2.part1.organizerId > 0 && 
				(
					form.step2.part1.teacher.search.id ||
					form.step2.part1.teacher.free.fullName
				)
			),
		};
	};
	const eventResumeData = useResumeData({
		loadingRequest,
		dataRelatedToForm,
		setForm: form => {
			dispatch({
				type: TYPES.SET_FORM,
				payload: { form },
			});
		},
		loadedDataRelatedToEventForm,
		toStep,
		isCompletedForm,
	});
	//#region Effects
	useEffect(() => {
		if (isCreate) eventsSessionStorage.eventId.remove();
		fetchData();
	}, []);
	//#endregion
	//#region Functions
	const changeForm = (type, entries) => {
		dispatch({
			type,
			payload: { entries },
		});
	};
	const changeFormStep1 = (part, field, value, errorValue) => {
		changeForm(TYPES.SET_FORM_STEP_1, { part, field, value, errorValue });
		if (!["hasSubtype", "eventSubtypeId"].includes(field))
			setCanShowDialogLeavingPage(true);
	};
	const changeFormStep2 = (part, field, value, errorValue) => {
		changeForm(TYPES.SET_FORM_STEP_2, { part, field, value, errorValue });
		setCanShowDialogLeavingPage(true);
	};
	const changeErrorText = (field, text) =>
		dispatch({
			type: TYPES.SET_ERROR_TEXT,
			payload: {
				field,
				text,
			},
		});
	//#endregion
	const { currentStep, form, errors, errorTexts } = state;
	const views = [
		<Step1
			changeForm={changeFormStep1}
			form={form.step1}
			errors={errors.step1}
			allDays={allDays}
			isCreate={isCreate}
			eventResumeData={eventResumeData.value}
			changePayGradeField={value =>
				changeFormStep2("part1", "payGradeId", value, false)
			}
		/>,
		<Step2
			eventResumeData={eventResumeData.value}
			form={form.step2}
			changeForm={changeFormStep2}
			changeErrorText={changeErrorText}
			errors={errors.step2}
			locations={locations}
			inscriptionEventTypes={inscriptionEventTypes}
			allCampus={allCampus}
			modalities={modalities}
			campusEnvironments={campusEnvironments}
			errorTexts={errorTexts}
			teacherZoomAccountDetail={{
				value: teacherZoomAccountDetail,
				set: setTeacherZoomAccountDetail,
			}}
		/>,
		<EventDetail
			dataRelatedToForm={dataRelatedToForm}
			eventResumeData={eventResumeData.value}
			toStep={toStep}
		/>,
	];
	const isDraft = eventsSessionStorage.isDraftEvent.get();
	const isWorkshopEventType =
		form.step1.part2.eventTypeId === EVENTS_CONSTANTS.EVENT_TYPES.WORK_SHOP;
	return (
		<EventFormContext.Provider
			value={{
				data: dataRelatedToForm,
				isCreate,
				loadingRequest,
				isWorkshopEventType,
			}}
		>
			<ContainerForm>
				<HelmetUTP
					title={`Eventos - Carga Manual | ${
						isCreate ? "Crear" : "Editar"
					}`}
				/>
				<ToastMessage {...toastMessageSaveEventAsDraft.value} />
				<ToastMessage
					variant="error"
					message="Te faltan completar campos o existen campos inválidos."
					hidden={!mustCorrect}
					toggleHidden={() => setMustCorrect(!mustCorrect)}
				/>
				<BackToView
					text="Volver a eventos"
					to={ABSOLUTE_PATHS.EVENT_LIST}
				/>
				{(isDraft === null || isDraft) && (
					<StepIndexer
						descriptions={STEPS_DESCRIPTIONS}
						currentStep={currentStep}
					/>
				)}
				<TitleForm>
					{isCreate ? "Creando" : "Editando"} Evento - Carga Manual
				</TitleForm>
				<ContentForm>
					{views[currentStep - 1] || views[views.length - 1]}
				</ContentForm>
				<Footer
					form={form}
					errors={errors}
					currentStep={currentStep}
					showToastMessageSaveEventAsDraft={
						toastMessageSaveEventAsDraft.show
					}
					stepsQuantity={STEPS_DESCRIPTIONS.length}
					setMustCorrect={setMustCorrect}
					nextStep={nextStep}
					toStep={toStep}
					changeErrorText={changeErrorText}
					changeErrorsStep={(type, value) =>
						dispatch({ type, payload: { value } })
					}
					createEvent={isCreate}
					canShowDialogLeavingPage={canShowDialogLeavingPage}
					setCanShowDialogLeavingPage={setCanShowDialogLeavingPage}
					isCompletedForm={isCompletedForm}
					eventResumeData={eventResumeData}
					teacherZoomAccountDetail={teacherZoomAccountDetail}
				/>
			</ContainerForm>
		</EventFormContext.Provider>
	);
};

export default Form;
